import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { db } from '../firebaseConfig';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { useAuth } from '../context/AuthContext';
import { IoArrowBack, IoCarSportOutline, IoBarcodeOutline, IoTimeOutline, IoCheckmarkCircleOutline } from 'react-icons/io5';
import './EditarVehiculoScreen.css'; // --- Premium Styles ---

function EditarVehiculoScreen() {
  const navigate = useNavigate();
  const { vehiculoId } = useParams();
  const { currentUser } = useAuth();

  const [loading, setLoading] = useState(true); // Start loading true
  const [error, setError] = useState('');

  // Form State
  const [marca, setMarca] = useState('');
  const [modelo, setModelo] = useState('');
  const [tipo, setTipo] = useState('auto'); // New Field
  const [placas, setPlacas] = useState('');
  const [color, setColor] = useState('');
  const [anio, setAnio] = useState('');
  const [estatus, setEstatus] = useState('pendiente'); // For display only

  // 1. Load Data
  useEffect(() => {
    if (!vehiculoId || !currentUser) return;

    const fetchVehiculo = async () => {
      setLoading(true);
      try {
        const docRef = doc(db, "vehiculos", vehiculoId);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          const data = docSnap.data();

          if (data.residenteId !== currentUser.uid) {
            setError("No tienes permiso para editar este vehículo.");
            setLoading(false);
            return;
          }

          if (data.estatus !== 'pendiente') {
            // If not pending, we technically can't edit. 
            // But let's load data for Read-Only view or just show error.
            // The user requirements imply "Editing".
            setError("Este vehículo ya fue validado y no puede ser modificado.");
            setLoading(false);
            return;
          }

          setMarca(data.marca || '');
          setModelo(data.modelo || '');
          setTipo(data.tipo || 'auto');
          setPlacas(data.placas || '');
          setColor(data.color || '');
          setAnio(data.anio || '');
          setEstatus(data.estatus || 'pendiente');

        } else {
          setError("No se encontró el vehículo.");
        }
      } catch (e) {
        setError("Error al cargar datos.");
        console.error(e);
      }
      setLoading(false);
    };

    fetchVehiculo();
  }, [vehiculoId, currentUser]);

  // 2. Save Changes
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!marca || !placas) {
      setError("La Marca y las Placas son obligatorias.");
      return;
    }

    setLoading(true);
    setError('');

    try {
      const docRef = doc(db, "vehiculos", vehiculoId);

      await updateDoc(docRef, {
        marca: marca,
        modelo: modelo,
        tipo: tipo, // Save type
        placas: placas.toUpperCase(),
        color: color,
        anio: anio,
        // Estatus remains same
      });

      alert("¡Vehículo actualizado con éxito!");
      navigate('/vehiculos');

    } catch (e) {
      console.error("Error updating: ", e);
      setError("Error al guardar los cambios.");
      setLoading(false);
    }
  };

  // --- Render ---

  if (loading && !marca) return <div className="vehicle-edit-container"><p>Cargando...</p></div>;

  return (
    <div className="vehicle-edit-container">

      <div className="vehicle-edit-header">
        <button onClick={() => navigate('/vehiculos')} className="vehicle-back-button">
          <IoArrowBack />
        </button>
        <h1 className="vehicle-header-title">Editar Vehículo</h1>
      </div>

      <form onSubmit={handleSubmit} className="vehicle-form-card">

        {error && <div className="error-msg" style={{ margin: '20px 30px 0' }}>{error}</div>}

        {/* If we have an error (like 'already validated'), we disable the form or hide buttons */}
        <fieldset disabled={!!error} style={{ border: 'none', padding: 0, margin: 0 }}>
          <div className="vehicle-form-grid">

            {/* Left Column: Vehicle Data */}
            <div className="form-column">
              <div className="vehicle-section-title">
                <IoCarSportOutline size={20} />
                Datos del Vehículo
              </div>

              <div className="vehicle-form-group">
                <label className="vehicle-label">Placas (Matrícula)</label>
                <input
                  type="text"
                  className="vehicle-input placas-input"
                  value={placas}
                  onChange={(e) => setPlacas(e.target.value)}
                  required
                />
              </div>

              <div className="vehicle-form-group">
                <label className="vehicle-label">Tipo de Vehículo</label>
                <select
                  className="vehicle-select"
                  value={tipo}
                  onChange={(e) => setTipo(e.target.value)}
                >
                  <option value="auto">Automóvil / Sedán</option>
                  <option value="suv">SUV / Camioneta</option>
                  <option value="moto">Motocicleta</option>
                  <option value="camion">Camión / Van</option>
                </select>
              </div>

              <div className="vehicle-form-group">
                <label className="vehicle-label">Marca</label>
                <input
                  type="text"
                  className="vehicle-input"
                  value={marca}
                  onChange={(e) => setMarca(e.target.value)}
                  required
                />
              </div>

              <div className="vehicle-form-group">
                <label className="vehicle-label">Modelo</label>
                <input
                  type="text"
                  className="vehicle-input"
                  value={modelo}
                  onChange={(e) => setModelo(e.target.value)}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                <div className="vehicle-form-group">
                  <label className="vehicle-label">Color</label>
                  <input
                    type="text"
                    className="vehicle-input"
                    value={color}
                    onChange={(e) => setColor(e.target.value)}
                  />
                </div>
                <div className="vehicle-form-group">
                  <label className="vehicle-label">Año</label>
                  <input
                    type="number"
                    className="vehicle-input"
                    value={anio}
                    onChange={(e) => setAnio(e.target.value)}
                  />
                </div>
              </div>
            </div>

            {/* Right Column: Status Widget (Read Only) */}
            <div className="form-column">
              <div className="vehicle-section-title">
                <IoBarcodeOutline size={20} />
                Estado del Registro
              </div>

              <div className={`status-widget ${estatus}`}>
                <div className="status-widget-icon">
                  {estatus === 'activo' ? <IoCheckmarkCircleOutline /> : <IoTimeOutline />}
                </div>
                <div className="status-widget-label">Estatus Actual</div>
                <div className="status-widget-value">
                  {estatus === 'activo' ? 'ACTIVO (Validado)' : 'PENDIENTE DE VALIDACIÓN'}
                </div>
              </div>

              <p style={{ fontSize: '0.85rem', color: '#6b7280', marginTop: '20px', lineHeight: '1.5' }}>
                <strong>Nota:</strong> Solo puedes editar los datos mientras el estatus sea "Pendiente".
                Una vez validado por la administración, cualquier cambio requerirá una nueva solicitud o contactar a administración.
              </p>
            </div>

          </div>

          <div className="vehicle-form-actions">
            <button type="button" onClick={() => navigate('/vehiculos')} className="btn-cancel">
              Cancelar
            </button>
            <button type="submit" className="btn-save" disabled={loading || !!error}>
              {loading ? 'Guardando...' : 'Guardar Cambios'}
            </button>
          </div>
        </fieldset>

      </form>
    </div>
  );
}

export default EditarVehiculoScreen;